import React, { useState, useEffect } from "react";
import { Typography, Box, Stepper, Step, StepLabel, StepContent, Button, Link, Paper, FormControlLabel, Checkbox, FormControl } from "@material-ui/core";
import { UserProfile } from "api/user";
import { Club } from "api/clubs";
import { HelpscoutFab } from "components";
import { getIdentityVerification, patchSignContracts } from "api/owner/settings";
import { useSnackbar } from "notistack";
import config from "config";
import i18n from "./i18n";

// prop types
interface WelcomeViewProps {
    club: Club | null,
    userProfile: UserProfile | null,
    onFetchUserProfile: () => void,
}

// steps
const Steps = {
    OwnerOnly: -1,
    FirstHello: 0,
    Contracts: 1,
    IdentityVerification: 2,
    Done: 3,
};

// admin welcome view
const WelcomeView = ({ club, userProfile, onFetchUserProfile }: WelcomeViewProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const steps = [];

    // ui state
    const [acceptContracts, setAcceptContracts] = useState(false);
    const [activeStep, setActiveStep] = useState(Steps.FirstHello);

    const isCompany = club?.capabilities.includes("company");

    // club update (verification status in particular)
    useEffect(() => {
        if (club) {
            setAcceptContracts(club.contractsHaveBeenSigned);
            if (club.verificationStatus !== "none") { // already triggered identity verification
                if (club.contractsHaveBeenSigned) {
                    setActiveStep(club.verificationStatus === "verified" ? Steps.Done : Steps.IdentityVerification); // success or other
                } else {
                    setActiveStep(Steps.Contracts); // needs to sign contracts
                }
            }
        }
    }, [club]);

    // on next step
    const onNextStep = async () => {
        if (activeStep === Steps.IdentityVerification) {
            const { redirectUrl } = await getIdentityVerification({ clubId: club!.id, returnUrl: window.location.href });
            if (redirectUrl) {
                window.location.href = redirectUrl; // redirect to stripe
            } else {
                enqueueSnackbar(i18n.identityVerificationSkipped, { variant: "success" });
                onFetchUserProfile(); // already verified -> reload club
            }
        } else if (activeStep === Steps.Contracts && !club?.contractsHaveBeenSigned) {
            await patchSignContracts({ clubId: club!.id });
            setActiveStep(activeStep + 1);
            onFetchUserProfile();
        } else {
            setActiveStep(activeStep + 1);
        }
    };

    // step 1 - a first hello
    steps.push({
        title: i18n.firstHello,
        content: (
            <>
                <Typography paragraph>
                    {`Hallo ${userProfile?.firstName}!`}
                    <br />
                    {isCompany ? (
                        <>
                            {"In unserem "}
                            <Link href="https://help.playsports.world/category/238-unternehmen" target="_blank">Hilfecenter</Link>
                            {" findest eine kurze Anleitung für das Dashboard und Antworten auf die wichtigsten Fragen. "}
                        </>
                    ) : (
                        <>
                            {"Schön, dass du da bist. Wir wünschen dir viel Spaß mit PLAYSPORTS. "}
                            {"Anbei findest du nochmal unsere Admin-Anleitung zum Download. In unserem "}
                            <Link href="https://help.playsports.world/" target="_blank">Helpdesk</Link>
                            {" gibt es Antworten auf die wichtigsten Fragen. Dort findest du zudem Videos, wie zum Beispiel ein kurzes "}
                            <Link href="https://help.playsports.world/article/156-video-intro-in-die-app" target="_blank">App-Intro</Link>
                            {", um den Ball ins Rollen zu bringen. "}
                        </>
                    )}

                    {!isCompany && (
                        <>
                            <Link
                                // className={classes.documentLink}
                                href={config.documents.adminManual}
                                style={{ display: "block", margin: "8px" }}
                                target="_blank"
                            >
                                Dokument: Admin-Anleitung
                            </Link>
                        </>
                    )}

                </Typography>
                <Typography paragraph variant="body2">
                    &#9654; play
                    <br />
                    Deine PLAYSPORTS-Crew
                </Typography>
            </>
        ),
    });

    // step 2 - contracts
    steps.push({
        title: i18n.contracts,
        content: club?.contractsHaveBeenSigned ? (
            <Typography>{i18n.alreadyAccepted}</Typography>
        ) : (
            <>
                <Typography>
                    {i18n.contractsRequired}
                    <Link
                        href={config.documents.contractSharedResponsibility}
                        style={{ display: "block", margin: "8px" }}
                        target="_blank"
                    >
                        Dokument: Gemeinsame Verantwortung
                    </Link>
                </Typography>
                <FormControl fullWidth>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                color="primary"
                                onChange={(e) => setAcceptContracts(e.target.checked)}
                                value={acceptContracts}
                            />
                        )}
                        label={i18n.acceptContracts}
                    />
                </FormControl>
            </>
        ),
    });

    // step 3 - identity verification
    steps.push({
        error: club?.verificationStatus === "failed",
        title: i18n.identityVerification,
        content: club?.verificationStatus === "failed" ? (
            <Typography paragraph>{i18n.identityVerificationDesc.failed(i18n.identityVerificationFailedReasons[club.verificationFailureReason!] || club.verificationFailureReason)}</Typography>
        ) : (
            <Typography paragraph>{i18n.identityVerificationDesc[club?.verificationStatus!]}</Typography>
        ),
    });

    // role of user in current club
    const currentRole = userProfile?.memberships.find((m) => m.club.id === club?.id)?.userRole;

    // render
    const stepOrDisabled = (currentRole === "owner") ? activeStep : Steps.OwnerOnly;
    return (
        <>
            <HelpscoutFab club={club} />

            {/* header */}
            <Box style={{ alignItems: "center", display: "flex", justifyContent: "space-between", padding: "16px" }}>
                <Typography variant="h6">
                    {i18n.welcome}
                </Typography>
            </Box>

            {/* steps */}
            {(currentRole !== "owner") && (
                <Paper square elevation={0} style={{ padding: "24px" }}>
                    <Typography>{i18n.contactOwner}</Typography>
                </Paper>
            )}
            <Stepper activeStep={stepOrDisabled} orientation="vertical">
                {steps.map(({ error, title, content }) => (
                    <Step key={title}>
                        <StepLabel error={error}>{title}</StepLabel>
                        <StepContent>
                            {content}
                            <Button
                                disabled={activeStep === Steps.FirstHello}
                                style={{ margin: "16px 16px 16px 0" }}
                                onClick={() => setActiveStep(activeStep - 1)}
                            >
                                {i18n.back}
                            </Button>
                            <Button
                                color="primary"
                                disabled={((activeStep === Steps.IdentityVerification) && ["processing", "verified"].includes(club?.verificationStatus!))
                                    || ((activeStep === Steps.Contracts) && !acceptContracts)}
                                style={{ margin: "16px 16px 16px 0" }}
                                variant="contained"
                                onClick={onNextStep}
                            >
                                {(activeStep === Steps.IdentityVerification && club?.verificationStatus === "failed") ? i18n.retry : i18n.next}
                            </Button>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
        </>
    );
};

export default WelcomeView;
